import { useState, useEffect, useCallback } from 'react';
import { UserPlus, X, Loader2, Crown, Shield, Pencil, Eye, ChevronDown, ArrowUpCircle } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { useProject } from '../../contexts/ProjectContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePlanLimits } from '../../hooks/usePlanLimits';
import {
  getProjectMembers, inviteMember, updateMemberRole, removeMember,
} from '../../services/projectMemberService';
import analytics from '../../services/analyticsService';
import type { ProjectMemberWithUser, ProjectMemberRole } from '../../types';
import { toast } from 'sonner';
import PricingPage from '../billing/PricingPage';
import { useCopy, fill } from '../../i18n';
import { plural } from '../../i18n/format';

interface ProjectShareModalProps {
  onClose: () => void;
}

/**
 * O papel (`owner`/`admin`/`editor`/`viewer`) é o valor gravado em
 * `project_members.role` e comparado por todo o modal: fica no código. Aqui
 * mora só o ícone; rótulo e descrição vêm do dicionário, pela mesma chave.
 */
const ROLE_ICONS: Record<ProjectMemberRole, typeof Eye> = {
  owner: Crown,
  admin: Shield,
  editor: Pencil,
  viewer: Eye,
};

function getInitials(name: string | null | undefined, email: string): string {
  if (name) return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  return email.slice(0, 2).toUpperCase();
}

function Avatar({ name, email, size = 32 }: { name?: string | null; email: string; size?: number }) {
  const colors = ['#6366f1','#8b5cf6','#ec4899','#f59e0b','#10b981','#3b82f6'];
  const color = colors[(email.charCodeAt(0) || 0) % colors.length];
  return (
    <div
      className="rounded-full flex items-center justify-center text-white font-semibold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.35, backgroundColor: color }}
    >
      {getInitials(name, email)}
    </div>
  );
}

export default function ProjectShareModal({ onClose }: ProjectShareModalProps) {
  const t = useCopy().modals.projectShare;
  const { project } = useProject();
  const { user } = useAuth();
  const { canAddMemberToProject, limits } = usePlanLimits();
  const [members, setMembers] = useState<ProjectMemberWithUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<ProjectMemberRole>('editor');
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [showPricing, setShowPricing] = useState(false);

  const memberLimit = limits.members_per_project; // -1 = ilimitado
  const isAtMemberLimit = !loading && !canAddMemberToProject(members.length);

  const load = useCallback(async () => {
    if (!project) return;
    setLoading(true);
    const { data } = await getProjectMembers(project.id);
    setMembers(data || []);
    setLoading(false);
  }, [project]);

  useEffect(() => { load(); }, [load]);

  const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!project || !user || !email.trim()) return;
    if (!isValidEmail(email.trim())) {
      toast.error(t.toasts.invalidEmail);
      return;
    }
    setAdding(true);
    const result = await inviteMember(project.id, email.trim(), role, user.id);
    if (result.error) {
      toast.error((result.error as { message: string }).message || t.toasts.addError);
    } else if (result.invited) {
      analytics.trackInviteSent(role, true);
      if (result.emailSent === false) {
        toast.warning(fill(t.toasts.inviteEmailFailed, { email: email.trim() }));
      } else {
        toast.success(fill(t.toasts.inviteSent, { email: email.trim() }));
      }
      setEmail('');
      await load();
    } else {
      toast.success(t.toasts.memberAdded);
      setEmail('');
      await load();
    }
    if (!result.error) analytics.trackProjectShared();
    setAdding(false);
  };

  const handleRoleChange = async (member: ProjectMemberWithUser, newRole: ProjectMemberRole) => {
    if (!project) return;
    const { error } = await updateMemberRole(project.id, member.user_id, newRole);
    if (error) {
      toast.error(t.toasts.roleError);
    } else {
      setMembers(prev => prev.map(m => m.id === member.id ? { ...m, role: newRole } : m));
    }
  };

  const handleRemove = async (member: ProjectMemberWithUser) => {
    if (!project) return;
    setRemovingId(member.id);
    const { error } = await removeMember(project.id, member.user_id);
    if (error) {
      toast.error(t.toasts.removeError);
    } else {
      setMembers(prev => prev.filter(m => m.id !== member.id));
      toast.success(t.toasts.memberRemoved);
    }
    setRemovingId(null);
  };

  const isCurrentUser = (member: ProjectMemberWithUser) => member.user_id === user?.id;
  const currentUserMember = members.find(m => m.user_id === user?.id);
  const canManage = currentUserMember?.role === 'owner' || currentUserMember?.role === 'admin';

  return (
    <>
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader className="flex-row items-center gap-3 space-y-0 pb-1">
          <div className="w-10 h-10 rounded-full bg-[var(--color-primary)]/10 flex items-center justify-center shrink-0">
            <UserPlus size={20} className="text-[var(--color-primary)]" />
          </div>
          <div className="flex flex-col min-w-0">
            <DialogTitle className="text-base leading-tight">{t.title}</DialogTitle>
            <DialogDescription className="truncate max-w-[260px] mt-0.5">
              {project?.name}
            </DialogDescription>
          </div>
        </DialogHeader>

        {/* Add member form */}
        {canManage && (
          <div className="flex flex-col gap-2 mt-2">
            <form onSubmit={handleAdd} className="flex gap-2">
              <Input
                type="email"
                placeholder={t.emailPlaceholder}
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="flex-1 text-sm"
                disabled={adding || isAtMemberLimit}
              />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant="outline" size="sm" className="shrink-0 gap-1 px-3" disabled={isAtMemberLimit}>
                    {t.roles[role].label}
                    <ChevronDown size={13} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[180px]">
                  {((['editor', 'viewer', 'admin'] as ProjectMemberRole[])).map(r => (
                    <DropdownMenuItem key={r} onClick={() => setRole(r)} className="flex flex-col items-start">
                      <span className="font-medium">{t.roles[r].label}</span>
                      <span className="text-xs text-[var(--color-text-muted)]">{t.roles[r].description}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button type="submit" variant="primary" size="sm" disabled={adding || !email.trim() || isAtMemberLimit}>
                {adding ? <Loader2 size={14} className="animate-spin" /> : t.add}
              </Button>
            </form>

            {isAtMemberLimit && (
              <div className="flex items-center justify-between px-3 py-2 rounded-[var(--radius-sm)] bg-amber-50 border border-amber-200 text-amber-700 text-xs">
                <span>{fill(plural(memberLimit, t.limitReached), { count: memberLimit })}</span>
                <button
                  type="button"
                  onClick={() => setShowPricing(true)}
                  className="flex items-center gap-1 font-medium text-[var(--color-primary)] hover:underline ml-3 shrink-0"
                >
                  <ArrowUpCircle size={13} />
                  {t.upgrade}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Member count indicator */}
        {!loading && memberLimit !== -1 && (
          <div className="flex justify-end mt-1">
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              isAtMemberLimit
                ? 'bg-amber-100 text-amber-700'
                : 'bg-[var(--color-bg-secondary)] text-[var(--color-text-muted)]'
            }`}>
              {fill(t.memberCount, { count: members.length, limit: memberLimit })}
            </span>
          </div>
        )}

        {/* Members list */}
        <div className="mt-1 flex flex-col gap-0.5 max-h-[300px] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 size={20} className="animate-spin text-[var(--color-text-muted)]" />
            </div>
          ) : members.length === 0 ? (
            <p className="text-sm text-[var(--color-text-muted)] text-center py-6">
              {t.empty}
            </p>
          ) : (
            members.map(member => {
              const RoleIcon = ROLE_ICONS[member.role] || Eye;
              const isSelf = isCurrentUser(member);
              const isOwner = member.role === 'owner';
              return (
                <div
                  key={member.id}
                  className="flex items-center gap-3 py-2 px-2 rounded-[var(--radius-sm)] hover:bg-[var(--color-bg-secondary)] group"
                >
                  <Avatar name={member.user?.full_name} email={member.user?.email || ''} size={34} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[var(--color-text)] truncate">
                      {member.user?.full_name || member.user?.email || t.unknownUser}
                      {isSelf && <span className="ml-1.5 text-xs text-[var(--color-text-muted)] font-normal">{t.you}</span>}
                    </p>
                    <p className="text-xs text-[var(--color-text-muted)] truncate">{member.user?.email}</p>
                  </div>

                  {/* Role control */}
                  {canManage && !isSelf && !isOwner ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="flex items-center gap-1 text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-text)] px-2 py-1 rounded hover:bg-[var(--color-bg-hover)] transition-colors">
                          <RoleIcon size={13} />
                          {t.roles[member.role]?.label}
                          <ChevronDown size={11} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="min-w-[180px]">
                        {((['editor', 'viewer', 'admin'] as ProjectMemberRole[])).map(r => (
                          <DropdownMenuItem
                            key={r}
                            onClick={() => handleRoleChange(member, r)}
                            className={member.role === r ? 'bg-[var(--color-bg-secondary)]' : ''}
                          >
                            <div>
                              <p className="font-medium">{t.roles[r].label}</p>
                              <p className="text-xs text-[var(--color-text-muted)]">{t.roles[r].description}</p>
                            </div>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <span className="flex items-center gap-1 text-xs text-[var(--color-text-muted)] px-2">
                      <RoleIcon size={13} />
                      {t.roles[member.role]?.label}
                    </span>
                  )}

                  {/* Remove button */}
                  {canManage && !isSelf && !isOwner && (
                    <button
                      onClick={() => handleRemove(member)}
                      disabled={removingId === member.id}
                      className="mouse:opacity-0 mouse:group-hover:opacity-100 focus-visible:opacity-100 transition-opacity p-1 rounded hover:bg-[var(--color-danger-bg)] hover:text-red-500 text-[var(--color-text-muted)]"
                    >
                      {removingId === member.id
                        ? <Loader2 size={14} className="animate-spin" />
                        : <X size={14} />}
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>

    {showPricing && <PricingPage onClose={() => setShowPricing(false)} />}
    </>
  );
}
